import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  apiRequest,
  formatDate,
  getImageUrl,
  getProfileImage,
} from "../../../core/common/api";
import { hasPermission } from "../../../core/common/adminPermissions";
import AppImage from "../../../shared/AppImage";

const DOCUMENT_LABELS: Record<string, string> = {
  gst_certificate: "GST Certificate",
  udyam_certificate: "Udyam Certificate",
  shop_license: "Shop License",
  self_declaration: "Self Declaration",
};

const STATUS_TONE: Record<string, string> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};

const KycDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [kyc, setKyc] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);

  const loadKyc = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const result = await apiRequest(`/kyc/${id}`, "GET");
      setKyc(result.kyc);
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to load KYC", "error");
      navigate("/kyc");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    void loadKyc();
  }, [loadKyc]);

  const reviewKyc = async (action: "approve" | "reject") => {
    if (!kyc?._id) return;

    let rejectionReason = "";

    if (action === "reject") {
      const result = await Swal.fire({
        title: "Reject KYC",
        input: "textarea",
        inputLabel: "Rejection reason",
        inputPlaceholder: "Write why this KYC is rejected...",
        inputAttributes: { maxlength: "500" },
        showCancelButton: true,
        confirmButtonText: "Reject",
        confirmButtonColor: "#dc2626",
        inputValidator: (value) => {
          if (!value?.trim()) return "Rejection reason is required";
          return null;
        },
      });

      if (!result.isConfirmed) return;
      rejectionReason = String(result.value || "").trim();
    } else {
      const confirm = await Swal.fire({
        title: "Approve KYC?",
        text: `${kyc.user?.name || "User"} will be able to place orders after approval.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Approve",
      });
      if (!confirm.isConfirmed) return;
    }

    setReviewing(true);
    try {
      const result = await apiRequest(`/kyc/${kyc._id}/review`, "PUT", {
        action,
        rejectionReason,
      });
      setKyc(result.kyc);
      await Swal.fire({
        title: action === "approve" ? "KYC Approved" : "KYC Rejected",
        text: result.message,
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to review KYC", "error");
    } finally {
      setReviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="page-section loading-state">
        <h2>Loading KYC…</h2>
      </div>
    );
  }

  if (!kyc) return null;

  const user = kyc.user;
  const image = getImageUrl(kyc.documentImage);
  const documentLabel = DOCUMENT_LABELS[kyc.documentType] || kyc.documentType;
  const statusLabel = STATUS_LABELS[kyc.status] || kyc.status;
  const isPending = kyc.status === "pending";

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <Link to="/kyc" className="btn-sm btn-outline back-link">
            ← Back to KYC
          </Link>
          <div className="page-eyebrow">KYC Submission</div>
          <h1 className="page-title">{user?.name || "Customer"}</h1>
          <div className="page-sub">
            {documentLabel} · Submitted {formatDate(kyc.submittedAt || kyc.createdAt)}
          </div>
        </div>
        <div className="page-hdr-right">
          <span className={`status-badge ${STATUS_TONE[kyc.status] || "muted"}`}>
            {statusLabel}
          </span>
          {isPending ? (
            <>
              <button
                type="button"
                className="btn-sm btn-success"
                disabled={reviewing}
                onClick={() => void reviewKyc("approve")}
              >
                {reviewing ? "Saving..." : "Approve"}
              </button>
              <button
                type="button"
                className="btn-sm btn-danger"
                disabled={reviewing}
                onClick={() => void reviewKyc("reject")}
              >
                Reject
              </button>
            </>
          ) : null}
        </div>
      </div>

      {isPending ? (
        <div className="terminal-banner is-warning">
          <div>
            <span className="status-badge warning">Action required</span>
            <p>
              This customer cannot place orders until KYC is approved. Open the
              document full size to verify authenticity.
            </p>
          </div>
        </div>
      ) : null}

      {kyc.status === "approved" ? (
        <div className="terminal-banner is-success">
          <div>
            <span className="status-badge success">Approved</span>
            <p>
              Reviewed {formatDate(kyc.reviewedAt)}
              {kyc.reviewedBy?.name ? ` by ${kyc.reviewedBy.name}` : ""}. This
              customer can place orders.
            </p>
          </div>
        </div>
      ) : null}

      {kyc.status === "rejected" ? (
        <div className="terminal-banner is-danger">
          <div>
            <span className="status-badge danger">Rejected</span>
            <p>
              {kyc.rejectionReason || "No rejection reason recorded."}
              {kyc.reviewedBy?.name
                ? ` · ${kyc.reviewedBy.name} · ${formatDate(kyc.reviewedAt)}`
                : kyc.reviewedAt
                  ? ` · ${formatDate(kyc.reviewedAt)}`
                  : ""}
            </p>
          </div>
        </div>
      ) : null}

      <div className="kyc-detail-grid">
        <section className="table-card info-card">
          <h3 className="info-card-title">Customer</h3>
          {user ? (
            <>
              <div className="img-name-cell">
                <AppImage
                  src={getProfileImage(user)}
                  alt={user.name || "Customer"}
                  crossOrigin="anonymous"
                />
                <div>
                  <strong>{user.name || "Customer"}</strong>
                  <small className="table-muted">{user.email || "—"}</small>
                  {user.phone ? (
                    <small className="table-muted">{user.phone}</small>
                  ) : null}
                </div>
              </div>

              <div className="profile-meta">
                <div className="profile-meta-row">
                  <span>Document</span>
                  <strong>{documentLabel}</strong>
                </div>
                <div className="profile-meta-row">
                  <span>Status</span>
                  <strong>
                    <span
                      className={`status-badge ${STATUS_TONE[kyc.status] || "muted"}`}
                    >
                      {statusLabel}
                    </span>
                  </strong>
                </div>
                <div className="profile-meta-row">
                  <span>Submitted</span>
                  <strong>{formatDate(kyc.submittedAt || kyc.createdAt)}</strong>
                </div>
                {kyc.reviewedAt ? (
                  <div className="profile-meta-row">
                    <span>Reviewed</span>
                    <strong>{formatDate(kyc.reviewedAt)}</strong>
                  </div>
                ) : null}
                {kyc.reviewedBy?.name ? (
                  <div className="profile-meta-row">
                    <span>Reviewed By</span>
                    <strong>{kyc.reviewedBy.name}</strong>
                  </div>
                ) : null}
              </div>

              {user._id && hasPermission("users") ? (
                <div className="info-card-actions">
                  <Link to={`/users/${user._id}`} className="btn-sm btn-outline">
                    View Profile
                  </Link>
                </div>
              ) : null}
            </>
          ) : (
            <p className="kyc-document-empty">Customer not available</p>
          )}
        </section>

        <section className="table-card">
          <div className="table-head">
            <h2>Document Image</h2>
            <p>
              {image
                ? "Click the image to open full size"
                : "No document image uploaded"}
            </p>
          </div>
          <div className="kyc-document-preview">
            {image ? (
              <a href={image} target="_blank" rel="noreferrer">
                <AppImage
                  src={image}
                  alt={`${documentLabel} document`}
                  className="kyc-document-image"
                  crossOrigin="anonymous"
                />
              </a>
            ) : (
              <p className="kyc-document-empty">No document image uploaded.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default KycDetail;
